import React, { useEffect, useState, useMemo, useRef } from "react";
import Navbar from "./Navbar";
import { useAuth } from "../hooks/useAuth";

import { useNavigate, useLocation } from "react-router-dom";
import {
  Info,
  Gift,
  LogOut,
  Home,
  CircleUserRound,
  DatabaseIcon,
  MessageSquareText,
  MessageCircleQuestion,
} from "lucide-react";
import { useFlow } from "../hooks/useFlow";
import RewardsService from "../lib/RewardsService";

const SidebarLink = ({ label, onClick, active = false, disabled = false }) => {
  let Icon = null;

  switch (label) {
    case "Home":
      Icon = Home;
      break;
    case "My Data":
      Icon = DatabaseIcon;
      break;
    case "My Profile":
      Icon = CircleUserRound;
      break;
    case "Rewards":
      Icon = Gift;
      break;
    case "FAQ & Support":
      Icon = MessageCircleQuestion;
      break;
    case "Contact Us":
      Icon = MessageSquareText;
      break;
    case "About Us":
      Icon = Info;
      break;
    case "Log-Out":
      Icon = LogOut;
      break;
    default:
      Icon = null;
  }
  const isDisabled = disabled === true || disabled === "true" || disabled === 1;

  return (
    <button
      type="button"
      disabled={isDisabled}
      aria-disabled={isDisabled}
      onClick={isDisabled ? undefined : onClick}
      className={`w-full flex items-center justify-start gap-3 text-left text-base rounded-lg px-3 py-2 transition-colors group
        ${
          isDisabled
            ? "text-gray-400 bg-transparent cursor-not-allowed opacity-60"
            : active
            ? "text-crimsonGlory bg-gray-50"
            : "text-gray-700 hover:text-crimsonGlory hover:bg-gray-50"
        }`}
    >
      {Icon && (
        <Icon
          className={`!w-[22px] !h-[22px] transition-colors ${
            active
              ? "text-crimsonGlory"
              : "text-gray-400 group-hover:text-crimsonGlory"
          }`}
        />
      )}
      <span>{label}</span>
    </button>
  );
};

const SidebarContent = ({
  initial,
  displayName,
  email,
  activeLabel,
  onNavigate,
  onLogout,
  disableRewards,
}) => {
  const { isExtendedWarranty, isInsuranceRenewal, isRefinancing } = useFlow();
  const myDataPath = "/my-data";
  let homePath = "/onboarding/survey";
  if (isExtendedWarranty()) {
    homePath = "/extended-warranty/survey";
  } else if (isRefinancing()) {
    homePath = "/refinance/refinance-survey";
  } else if (isInsuranceRenewal()) {
    homePath = "/renewal/survey";
  }
  return (
    <div className="flex flex-col w-full h-full px-6 py-8 justify-between">
      <div className="space-y-6">
        {/* User Info Header */}
        <div className="flex flex-col items-center text-center space-y-3">
          <div className="w-16 h-16 rounded-full bg-crimsonGlory/10 flex items-center justify-center text-crimsonGlory text-2xl font-bold">
            {initial}
          </div>
          <div className="space-y-0.5 max-w-[10rem]">
            <div className="font-semibold text-gray-900 text-sm truncate">
              {displayName || "Guest"}
            </div>
            {email && (
              <div className="text-xs text-gray-500 truncate">{email}</div>
            )}
          </div>
        </div>

        {/* Navigation Links */}
        <nav className="space-y-1.5">
          <SidebarLink
            label="Home"
            onClick={() => onNavigate(homePath)}
            active={activeLabel === "Home"}
          />
          <SidebarLink
            label="My Data"
            onClick={() => onNavigate(myDataPath)}
            active={activeLabel === "My Data"}
          />
          {/* <SidebarLink
            label="My Profile"
            onClick={() => onNavigate("/contact-info")}
            active={activeLabel === "My Profile"}
          /> */}
          <SidebarLink
            label="Rewards"
            onClick={() => onNavigate("/rewards")}
            active={activeLabel === "Rewards"}
            disabled={disableRewards}
          />
          <SidebarLink
            label="Contact Us"
            onClick={() => {
              window.open(
                "https://support.ownli.co/hc/en-us/requests/new",
                "_blank",
              );
            }}
            active={activeLabel === "Contact Us"}
          />
          <SidebarLink label="Log-Out" onClick={onLogout} />
        </nav>
      </div>

      {/* Sidebar Footer */}
      <div className="pt-6 border-t border-gray-100 text-[11px] text-gray-500 space-y-1 flex gap-4 items-center justify-center">
        <img
          alt="Ownli"
          className="w-10 h-10 rounded-full"
          src="https://parked.blob.core.windows.net/ownli/www/logos/logo.png"
        />
        <div>
          <div className="text-black text-xs">Powered by Ownli 2026</div>
          <div className="space-x-1 text-xs">
            <a
              target="_blank"
              rel="noreferrer"
              className="underline text-crimsonGlory"
              href="https://support.ownli.co/hc/en-us/articles/360011275958-Terms-of-Use"
            >
              Terms of use
            </a>
            <span className="text-black">&</span>
            <a
              target="_blank"
              rel="noreferrer"
              className="underline text-crimsonGlory"
              href="https://support.ownli.co/hc/en-us/articles/360011276138-Privacy-policy"
            >
              Privacy policy
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

const MainLayout = ({
  children,
  rootClassName = "",
  footerClassName = "bg-gray-50",
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const mainRef = useRef(null);

  const { user, getIdToken, logout } = useAuth();
  const { config } = useFlow();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [disableRewards, setDisableRewards] = useState(false);

  useEffect(() => {
    if (disableRewards && location.pathname.startsWith("/rewards")) {
      //navigate("/onboarding/survey", { replace: true });
    }
  }, [disableRewards, location.pathname, navigate]);

  useEffect(() => {
    if (mainRef.current) {
      mainRef.current.scrollTop = 0;
    }
    window.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect(() => {
    const fetchRewards = async () => {
      if (user) {
        const token = await getIdToken();
        if (token) {
          const summary = await RewardsService.getRewardsSummary(token);
          let sum = 0;
          if (summary) {
            sum =
              (summary.pending || 0) +
              (summary.confirmed || 0) +
              (summary.total || 0);
          }
          setDisableRewards(sum <= 0);
        }
      }
    };
    //fetchRewards();
  }, []);

  const { displayName, email, initial } = useMemo(() => {
    let dName = "";
    let uEmail = "";

    try {
      const raw = sessionStorage.getItem("customerData");
      if (raw) {
        const customer = JSON.parse(raw);
        dName =
          customer.nameFull ||
          `${customer.nameFirst || ""} ${customer.nameLast || ""}`.trim();
        uEmail = customer.contactEmail || customer.loginEmail || "";
      }
    } catch (e) {
      console.error("Failed to parse customerData:", e);
    }

    if (!dName && user) {
      dName =
        user.displayName || user.email || user.phoneNumber || "My Profile";
    }

    if (!uEmail && user) {
      uEmail = user.email || "";
    }

    const init = (dName || "O").trim().charAt(0).toUpperCase();

    return { displayName: dName, email: uEmail, initial: init };
  }, [user]);

  const closeSidebarIfMobile = () => setIsSidebarOpen(false);
  const handleToggleSidebar = () => setIsSidebarOpen((prev) => !prev);

  const handleNavigation = (path) => {
    closeSidebarIfMobile();
    navigate(path);
  };

  const handleLogout = async () => {
    setIsSidebarOpen(false);
    try {
      await logout();
    } catch (err) {
      console.error("Error during logout:", err);
    } finally {
      sessionStorage.clear();
      localStorage.clear();
      window.location.href = config?.defaultRoute || "/onboarding/welcome";
    }
  };

  const getActiveLabel = () => {
    const pathname = location.pathname;
    if (pathname === "/my-data") return "My Data";
    if (pathname.startsWith("/rewards")) return "Rewards";
    if (pathname === "/contact-info") return "My Profile";
    if (pathname === "/contact-us") return "Contact Us";
    return null;
  };

  const activeLabel = getActiveLabel();
  const isAuthenticated = !!user;
  const showSidebar =
    isAuthenticated && !location.pathname.endsWith("/welcome");

  return (
    <div
      className={`min-h-screen h-screen flex flex-col overflow-hidden ${rootClassName}`}
    >
      <Navbar
        isSidebarOpen={showSidebar ? isSidebarOpen : false}
        onToggleSidebar={showSidebar ? handleToggleSidebar : undefined}
      />

      <div className="flex flex-1 overflow-hidden relative border-t-2 border-gray-100">
        {showSidebar && (
          <>
            {/* Desktop Sidebar */}
            <aside className="hidden md:flex w-64 border-r border-gray-100 bg-white flex-shrink-0 overflow-y-auto">
              <SidebarContent
                email={email}
                initial={initial}
                onLogout={handleLogout}
                displayName={displayName}
                activeLabel={activeLabel}
                onNavigate={handleNavigation}
                disableRewards={disableRewards}
              />
            </aside>

            {/* Mobile Sidebar Overlay */}
            {isSidebarOpen && (
              <div className="md:hidden fixed inset-0 z-40 flex">
                <div className="w-64 bg-white border-r border-gray-100 h-full overflow-y-auto">
                  <SidebarContent
                    email={email}
                    initial={initial}
                    onLogout={handleLogout}
                    displayName={displayName}
                    activeLabel={activeLabel}
                    onNavigate={handleNavigation}
                    disableRewards={disableRewards}
                  />
                </div>
                <button
                  type="button"
                  aria-label="Close menu"
                  onClick={handleToggleSidebar}
                  className="flex-1 bg-black/30 backdrop-blur-sm"
                />
              </div>
            )}
          </>
        )}

        <main ref={mainRef} className="flex-1 flex flex-col overflow-y-auto">
          <div className="w-full max-w-6xl mx-auto px-4 py-6">{children}</div>
        </main>
      </div>

      <footer
        className={`flex justify-between items-center px-6 py-2 mt-auto text-gray-500 text-xs ${footerClassName}`}
      >
        {/* Left side — Support link */}
        <p className="flex items-center space-x-2 ">
          <span>Need help?</span>
          <button
            onClick={() => {
              window.open(
                "https://support.ownli.co/hc/en-us/requests/new",
                "_blank",
              );
            }}
            className="text-blue-500 hover:text-blue-600 underline"
          >
            Contact Us.
          </button>
        </p>
        {/* Right side — Powered by Ownli */}
        <p className="flex items-center space-x-2">
          <span>Powered by</span>
          <img src="/logo.jpeg" alt="Ownli logo" className="h-3" />
        </p>
      </footer>
    </div>
  );
};

export default MainLayout;
